import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LucideIcon } from "lucide-react";
import {
  Briefcase,
  CalendarDays,
  FileText,
  Home,
  Mail,
  MessageCircle,
  PlusSquare,
  Workflow,
} from "lucide-react";

export type NavItem = {
  label: string;
  description: string;
  href: string;
  icon: LucideIcon;
  active: (pathname: string) => boolean;
  children?: Array<{
    label: string;
    href: string;
    active: (pathname: string) => boolean;
  }>;
};

export const useNavItems = (): NavItem[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        label: t("nav.aiStaff"),
        description: t("nav.departments"),
        href: "/ai-agents/insights",
        icon: Home,
        active: (pathname: string) => pathname === "/ai-agents/insights",
      },
      {
        label: t("nav.chat"),
        description: t("nav.chatDescription"),
        href: "/ai-agents",
        icon: MessageCircle,
        active: (pathname: string) =>
          pathname.startsWith("/ai-agents") &&
          pathname !== "/ai-agents/insights" &&
          pathname !== "/ai-agents/tasks" &&
          !pathname.startsWith("/ai-agents/projects"),
      },
      {
        label: t("nav.newTask"),
        description: t("nav.newTaskDescription"),
        href: "/new-task",
        icon: PlusSquare,
        active: (pathname: string) => pathname.startsWith("/new-task"),
      },
      {
        label: t("nav.autoTasks"),
        description: t("nav.autoTasksDescription"),
        href: "/auto-tasks",
        icon: Workflow,
        active: (pathname: string) =>
          pathname.startsWith("/auto-tasks") || pathname === "/ai-agents/tasks",
      },
      {
        label: t("nav.projects"),
        description: t("nav.projectsDescription"),
        href: "/projects",
        icon: Briefcase,
        active: (pathname: string) =>
          pathname.startsWith("/projects") || pathname.startsWith("/ai-agents/projects"),
      },
      {
        label: t("nav.meetingsMinutes"),
        description: t("nav.meetingsDescription"),
        href: "/meetings",
        icon: CalendarDays,
        active: (pathname: string) =>
          pathname.startsWith("/meetings") ||
          pathname.startsWith("/meeting/") ||
          pathname.startsWith("/upload") ||
          pathname.startsWith("/record") ||
          pathname.startsWith("/meeting-bot") ||
          pathname.startsWith("/meetings/audio-inbox") ||
          pathname.startsWith("/meeting-settings"),
        children: [
          {
            label: t("nav.allMeetings"),
            href: "/meetings",
            active: (pathname: string) =>
              (pathname.startsWith("/meetings") && !pathname.startsWith("/meetings/audio-inbox"))
              || pathname.startsWith("/meeting/"),
          },
          {
            label: t("nav.uploadAudio"),
            href: "/upload",
            active: (pathname: string) => pathname.startsWith("/upload"),
          },
          {
            label: t("nav.recordMeeting"),
            href: "/record",
            active: (pathname: string) => pathname.startsWith("/record"),
          },
          {
            label: t("nav.meetingBot"),
            href: "/meeting-bot",
            active: (pathname: string) => pathname.startsWith("/meeting-bot"),
          },
          {
            label: t("nav.storedAudio"),
            href: "/meetings/audio-inbox",
            active: (pathname: string) => pathname.startsWith("/meetings/audio-inbox"),
          },
          {
            label: t("nav.meetingSettings"),
            href: "/meeting-settings",
            active: (pathname: string) => pathname.startsWith("/meeting-settings"),
          },
        ],
      },
      {
        label: t("nav.contextHub"),
        description: t("nav.contextHubDescription"),
        href: "/documents",
        icon: FileText,
        active: (pathname: string) => pathname.startsWith("/documents"),
      },
      {
        label: t("nav.contact"),
        description: t("nav.contactDescription"),
        href: "/contact",
        icon: Mail,
        active: (pathname: string) => pathname.startsWith("/contact"),
      },
    ],
    [t],
  );
};
